import { combine, createDomain, sample } from 'effector'
import { createGate, useStoreMap } from 'effector-react'
import { Inspection } from '~/shared/api'
import { createCache } from '~/shared/lib/mapCacheFactory'

type InspectionsObj = {
  id: UniqueId
  inspection: Inspection
  previousInspection: Inspection | null
}

type InitData = {
  id: UniqueId
  needLoadPreviousInspections?: boolean
}

export const domain = createDomain('entities.inspection')
export const Gate = createGate<InitData>()

export const requestFx = domain.createEffect<InitData, InspectionsObj>({
  handler: fetchInspection,
})

const {
  $cache: $inspectionCache,
  useCache: useInspectionCache,
  updateCache,
} = createCache<InspectionsObj>({
  domain,
  getEntityId: (inspection) => inspection.id as UniqueId,
})
export { $inspectionCache, useInspectionCache }

$inspectionCache.on(requestFx.doneData, (cache, inspection) =>
  updateCache(cache, [inspection]),
)

export const $inspectionError = domain
  .createStore<Record<UniqueId, Error>>({})
  .on(requestFx.fail, (store, { error, params: { id } }) => ({
    [id]: error,
    ...store,
  }))

export const useInspectionError = (id: UniqueId) =>
  useStoreMap($inspectionError, (errors) => errors[id])

async function fetchInspection({ id, needLoadPreviousInspections }: InitData) {
  const inspectionRes = await Inspection.find(id)
  let previousInspectionRes = null

  if (needLoadPreviousInspections) {
    previousInspectionRes = await Inspection.getPreviousInspection(id)
  }

  return {
    id,
    inspection: inspectionRes.getData() as Inspection,
    previousInspection: previousInspectionRes,
  }
}

export const $id = domain
  .createStore<UniqueId | null>(null)
  .on(Gate.state, (_, { id }) => id)

export const $inspection = combine($id, $inspectionCache, (id, cache) => {
  if (!id) return null
  return cache.map[id]?.inspection ?? null
})

export const $previousInspection = combine(
  $id,
  $inspectionCache,
  (id, cache) => {
    if (!id) return null
    return cache.map[id]?.previousInspection ?? null
  },
)

sample({
  clock: $id,
  source: Gate.state,
  filter: (_, id) => Boolean(id),
  target: requestFx,
})
